import {
  RELABEL_WEBSITES,
  type BulkWebsiteRelabelRow,
  type RelabelTable,
  type RelabelWebsite,
} from "../../../api/profiles/websiteRelabelService.js";

/**
 * Parses the website-relabel manifest file (SOFTSUPP-532).
 *
 * Columns are mapped by NAME rather than position: the manifest carries up to
 * thirteen columns, the generator script may reorder or extend them, and
 * reading positionally would silently pair the wrong values. Only six columns
 * are required — the rest are diagnostic passthrough kept for the preview table
 * and the rollback manifest.
 *
 * Deliberately forgiving about how the file was produced, because both of
 * these happen in practice: pasting out of a spreadsheet yields tabs rather
 * than commas, and Excel's "CSV UTF-8" prepends a byte-order mark that would
 * otherwise make the first header read as "﻿table".
 */

export type ManifestRowProblem = "invalid-table" | "invalid-uuid" | "invalid-website";

export type ManifestRow = {
  /** 1-indexed line in the source file, counting the header, for error reporting. */
  lineNumber: number;
  table: string;
  id: string;
  currentWebsite: string;
  /** Empty cell → null: the API models "no language" as null, not "". */
  currentLanguage: string | null;
  targetWebsite: string;
  /** Empty cell → null: the API models "no language" as null, not "". */
  targetLanguage: string | null;
  sourceSite: string;
  sourcePostId: string;
  detectedLang: string;
  anomaly: string;
  statusHint: string;
  /**
   * Display name as the generator saw it, when the file carries a `name`
   * column. Absent for older manifests, hence optional rather than "".
   *
   * It is the only name available for firm rows: profiles-api deliberately
   * omits `preview.name` for firms — it has no clean path to address-book
   * company names — so without this every firm row would read as an em dash.
   *
   * Untrusted: this is file content the page did not author. Render as plain
   * text and defuse it on the way back out to CSV.
   */
  name?: string;
  /** Content excerpt from an optional `excerpt` column. Untrusted, as `name` is. */
  excerpt?: string;
  problem: ManifestRowProblem | null;
};

export type ParseManifestResult = {
  delimiter: "," | "\t";
  rows: ManifestRow[];
};

export type ParseManifestOptions = {
  /** Overrides the row ceiling. Mainly for tests. */
  maxRows?: number;
};

/**
 * Ceiling on rows accepted from one file. Matches the API's per-request cap,
 * so a file the parser accepts is a file the validate call can actually take —
 * and the preview table is not virtualised, so a much larger file would lock
 * the tab up rather than fail cleanly. The whole SOFTSUPP-532 batch is ~3,750
 * rows, comfortably inside it.
 */
export const MAX_ROWS = 5000;

/**
 * Ceiling on the BYTES of one file, checked before it is read.
 *
 * MAX_ROWS cannot do this job on its own: counting rows means having the whole
 * file in memory as a string first, so a multi-hundred-megabyte file picked by
 * mistake locks the tab up before the row limit ever gets a chance to speak.
 *
 * Sized off MAX_ROWS rather than picked from the air: a manifest row is ~120
 * bytes of columns, or ~1 KB once the optional `name` and `excerpt` carry real
 * profile copy, so 5,000 rows lands comfortably under 5 MB. Anything past that
 * is not a manifest.
 */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

function megabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

/**
 * Vets a file's size before anything reads it.
 *
 * @returns the operator-facing reason to refuse the file, or null to read on.
 */
export function fileSizeProblem(bytes: number): string | null {
  if (bytes <= MAX_FILE_BYTES) {
    return null;
  }
  return (
    `That file is ${megabytes(bytes)} MB — too large to read. The limit is ` +
    `${megabytes(MAX_FILE_BYTES)} MB, and a manifest at the ${MAX_ROWS.toLocaleString()} row cap ` +
    "is far smaller than that, so check you picked the right file."
  );
}

export const REQUIRED_COLUMNS = [
  "table",
  "id",
  "current_website",
  "current_lang",
  "target_website",
  "target_lang",
] as const;

/**
 * Mirrors zod v4's `z.uuid()` on profiles-api: version nibble 1-8 and variant
 * nibble 8/9/a/b. Same pattern the bulk external-references parser uses; the
 * nil UUID is likewise rejected because it can never match a profile and a
 * preview error is more useful than a not_found in the verdict table.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const RELABEL_TABLES = new Set<string>(["firm_profiles", "lawyer_profiles"]);

const WEBSITES = new Set<string>(RELABEL_WEBSITES);

function detectDelimiter(header: string): "," | "\t" {
  const tabs = header.split("\t").length - 1;
  const commas = header.split(",").length - 1;
  return tabs > commas ? "\t" : ",";
}

/**
 * Splits one line into fields, honouring double quotes. A quoted field may
 * contain the delimiter and `""` unescapes to a literal quote — anomaly notes
 * in the manifest carry commas, so naive splitting would shear those rows.
 */
function splitFields(line: string, delimiter: "," | "\t"): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (inQuotes) {
      if (character === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += character;
      }
    } else if (character === '"' && current === "") {
      inQuotes = true;
    } else if (character === delimiter) {
      fields.push(current);
      current = "";
    } else {
      current += character;
    }
  }
  fields.push(current);
  return fields;
}

function findProblem(row: ManifestRow): ManifestRowProblem | null {
  if (!RELABEL_TABLES.has(row.table)) {
    return "invalid-table";
  }
  if (!UUID_PATTERN.test(row.id)) {
    return "invalid-uuid";
  }
  if (!WEBSITES.has(row.currentWebsite) || !WEBSITES.has(row.targetWebsite)) {
    return "invalid-website";
  }
  return null;
}

/**
 * Human explanation of a row's problem, for the verdict table. Lives next to
 * the detection so the two cannot disagree about which field is at fault.
 */
export function problemDetail(row: ManifestRow): string | null {
  switch (row.problem) {
    case "invalid-table":
      return `table '${row.table}' is not firm_profiles or lawyer_profiles`;
    case "invalid-uuid":
      return `id '${row.id}' is not a valid UUID`;
    case "invalid-website":
      return WEBSITES.has(row.currentWebsite)
        ? `target_website '${row.targetWebsite}' is not a known site edition`
        : `current_website '${row.currentWebsite}' is not a known site edition`;
    default:
      return null;
  }
}

/**
 * Shapes a problem-free row for the API. Only safe after the problem check —
 * the casts hold exactly because `findProblem` vetted table and websites.
 */
export function toApiRow(row: ManifestRow): BulkWebsiteRelabelRow {
  return {
    table: row.table as RelabelTable,
    id: row.id,
    currentWebsite: row.currentWebsite as RelabelWebsite,
    currentLanguageCode: row.currentLanguage,
    targetWebsite: row.targetWebsite as RelabelWebsite,
    targetLanguageCode: row.targetLanguage,
  };
}

/** Trimmed cell, or "" when the column is absent from the file entirely. */
function cell(fields: string[], index: number | undefined): string {
  if (index === undefined) {
    return "";
  }
  return (fields[index] ?? "").trim();
}

/** The two language columns: an empty cell means "no language", which the API models as null. */
function langCell(fields: string[], index: number | undefined): string | null {
  const value = cell(fields, index);
  return value === "" ? null : value;
}

/**
 * A cell whose column may not exist at all. An absent column and a blank cell
 * are the same thing here — "the file said nothing" — so both collapse to
 * undefined, which is what lets the display fall back with `??`.
 */
function optionalCell(fields: string[], index: number | undefined): string | undefined {
  const value = cell(fields, index);
  return value === "" ? undefined : value;
}

export function parseManifest(
  text: string,
  options: ParseManifestOptions = {},
): ParseManifestResult {
  const lines = text
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .map((line, index) => ({ line, lineNumber: index + 1 }))
    .filter(entry => entry.line.trim() !== "");

  const header = lines.shift();
  if (!header) {
    throw new Error(
      `The file is empty. Expected a header row with at least: ${REQUIRED_COLUMNS.join(",")}`,
    );
  }

  const delimiter = detectDelimiter(header.line);
  const columnIndex = new Map<string, number>();
  splitFields(header.line, delimiter).forEach((name, index) => {
    // First occurrence wins, so a duplicated header cannot silently remap a column.
    const key = name.trim().toLowerCase();
    if (!columnIndex.has(key)) {
      columnIndex.set(key, index);
    }
  });

  const missing = REQUIRED_COLUMNS.filter(name => !columnIndex.has(name));
  if (missing.length > 0) {
    throw new Error(
      `The file is missing required column(s): ${missing.join(", ")}. ` +
        `Expected a header containing: ${REQUIRED_COLUMNS.join(",")}`,
    );
  }

  const maxRows = options.maxRows ?? MAX_ROWS;
  if (lines.length > maxRows) {
    throw new Error(
      `That file has ${lines.length.toLocaleString()} rows, above the ${maxRows.toLocaleString()} ` +
        "row limit for a single upload. Split it into smaller files and upload them in turn.",
    );
  }

  const column = (name: string) => columnIndex.get(name);

  const rows = lines.map(({ line, lineNumber }) => {
    const fields = splitFields(line, delimiter);
    const row: ManifestRow = {
      lineNumber,
      table: cell(fields, column("table")),
      id: cell(fields, column("id")),
      currentWebsite: cell(fields, column("current_website")),
      currentLanguage: langCell(fields, column("current_lang")),
      targetWebsite: cell(fields, column("target_website")),
      targetLanguage: langCell(fields, column("target_lang")),
      sourceSite: cell(fields, column("source_site")),
      sourcePostId: cell(fields, column("source_post_id")),
      detectedLang: cell(fields, column("detected_lang")),
      anomaly: cell(fields, column("anomaly")),
      statusHint: cell(fields, column("status_hint")),
      // Not in REQUIRED_COLUMNS on purpose: manifests generated before these
      // columns existed must still parse.
      name: optionalCell(fields, column("name")),
      excerpt: optionalCell(fields, column("excerpt")),
      problem: null,
    };
    row.problem = findProblem(row);
    return row;
  });

  return { delimiter, rows };
}
